'use client';
import { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import Typography from '../../typography/typography.component';
import { useTranslation } from 'react-i18next';
import { SearchIcon, X } from '../../../../../public/Images';
import pino from 'pino';
import Image from 'next/image';
import colors from '@/theme/colors.module.scss';

const logger = pino().child({
    s: 'src/components/shared/map/auto-complete/auto-complete.component.tsx',
});

const AutocompleteComponent = ({
    onPlaceChanged,
    value,
}: {
    onPlaceChanged: (result: google.maps.places.PlaceResult) => void;
    value?: string | null;
}) => {
    const { t, i18n } = useTranslation();
    const IS_RTL = i18n.language === 'ar';
    const [predictions, setPredictions] =
        useState<google.maps.places.AutocompletePrediction[]>();
    const [inputValue, setInputValue] = useState<string>(value || '');

    const placesService = useRef<google.maps.places.PlacesService>();

    const placeSelected = (placeId: string, address?: string) => {
        if (placesService.current) {
            // Access latitude and longitude as place.geometry.location.lat() and place.geometry.location.lng()
            placesService.current.getDetails(
                {
                    placeId,
                    language: i18n.language,
                },
                (place, status) => {
                    if (
                        status ===
                        window.google.maps.places.PlacesServiceStatus.OK
                    ) {
                        if (place) {
                            setInputValue(
                                address || (place.formatted_address as string)
                            );
                            onPlaceChanged(place);
                            setPredictions([]);
                        }
                    } else {
                        logger.info('Error fetching place details:', status);
                        logger.error(status);
                    }
                }
            );
        }
    };
    useEffect(() => {
        placesService.current = new window.google.maps.places.PlacesService(
            document.createElement('div')
        );
    }, []);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    const autocompleteService =
        useRef<google.maps.places.AutocompleteService>();

    autocompleteService.current =
        new window.google.maps.places.AutocompleteService();

    const fetchPredictions = (input: string) => {
        if (autocompleteService.current) {
            autocompleteService.current.getPlacePredictions(
                {
                    input,
                    componentRestrictions: {
                        country: 'EG',
                    },
                    language: i18n.language,
                },
                (predictions, status) => {
                    if (
                        status ===
                        window.google.maps.places.PlacesServiceStatus.OK
                    ) {
                        setPredictions(predictions || []);
                    } else {
                        logger.info('Error fetching predictions:', status);
                        logger.error(status);
                    }
                }
            );
        }
    };
    useEffect(() => {
        if (value) {
            setInputValue(value);
        }
    }, [value]);
    const clearSearch = () => {
        setInputValue('');
        setPredictions([]);
    };
    const handleInputChange = (input: string) => {
        if (input === '') {
            setInputValue('');
            setPredictions([]);
            return;
        }
        setInputValue(input);
        fetchPredictions(input);
    };

    return (
        // <div>
        //     <Autocomplete
        //         onLoad={onLoad}
        //         options={options}
        //         onPlaceChanged={handlePlaceChanged}
        //     >
        //         {inputComponent}
        //     </Autocomplete>
        // </div>
        <div className={styles.input_group_container}>
            <input
                type="text"
                placeholder={t('autoCompletePlaceholder') as string}
                value={inputValue}
                onChange={e => handleInputChange(e.target.value)}
                className={styles.input}
            />
            <Image
                className={styles.search_icon}
                src={SearchIcon.src}
                width={SearchIcon.width}
                height={SearchIcon.height}
                alt="serach location"
            />
            {!!inputValue.length && (
                <button
                    onClick={() => clearSearch()}
                    className={styles.clear_icon}
                    style={{
                        left: IS_RTL ? '10px' : '',
                        right: !IS_RTL ? '10px' : '',
                    }}
                >
                    <Image src={X.src} width={10} height={10} alt="clear" />
                </button>
            )}
            <ul
                className={`${styles.dropdown_container} scrollable ${
                    predictions?.length !== 0 ? styles.open : ''
                }`}
            >
                {predictions?.map((prediction, index) => (
                    <li key={index}>
                        <button
                            onClick={() =>
                                placeSelected(
                                    prediction?.place_id,
                                    prediction?.description
                                )
                            }
                            className={styles.button}
                        >
                            <Typography
                                variant="body2Medium"
                                color={colors.black}
                            >
                                {prediction?.structured_formatting?.main_text}
                            </Typography>
                            <Typography variant="caption" color={colors.black}>
                                {prediction.description}
                            </Typography>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default AutocompleteComponent;
